import { X } from "@/components/logos/X";
import {
  UserCircle,
  MapPin,
  Calendar,
  Users,
  Sparkles,
  CheckCircle,
  XCircle,
  Edit,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useApproveReplyMutation, useRejectReplyMutation } from "@/api/twitter";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Helper functions
function formatDate(
  dateString: string,
  options: Intl.DateTimeFormatOptions = {},
) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";
    const defaultOptions = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    } satisfies Intl.DateTimeFormatOptions;
    return date.toLocaleString(undefined, { ...defaultOptions, ...options });
  } catch (e) {
    console.error("Error formatting date:", e);
    return "Invalid date";
  }
}

function formatNumber(num: unknown) {
  const parsedNum = Number(num);
  if (isNaN(parsedNum) || parsedNum < 0) return "0";
  if (parsedNum >= 1000000) {
    return (parsedNum / 1000000).toFixed(1) + "M";
  } else if (parsedNum >= 1000) {
    return (parsedNum / 1000).toFixed(1) + "K";
  }
  return parsedNum.toString();
}

const InfluencerEngagementCard = ({
  reply,
  personaData,
  onApprove,
  onReject,
  automatedReplies,
}: any) => {
  const { toast } = useToast();
  const approveReplyMutation = useApproveReplyMutation();
  const rejectReplyMutation = useRejectReplyMutation();

  // State for editing the response
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState("");
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // Initialize edited response with the original response
  useState(() => {
    setEditedResponse(reply?.response_content || "");
  });

  // Open tweet in new tab
  const openTweetInNewTab = () => {
    if (reply.original_message?.id && reply.original_message?.sender_id) {
      window.open(
        `https://twitter.com/${reply.original_message.sender_id}/status/${reply.original_message.id}`,
        "_blank",
      );
    }
  };

  // Approve the reply
  const handleApprove = async () => {
    if (automatedReplies) {
      toast({
        description:
          "Automated replies are enabled. Replies are posted automatically.",
      });
      return;
    }

    try {
      setApproving(true);
      await approveReplyMutation.mutateAsync({
        docId: reply.doc_id,
        personaId: personaData.id,
        editedResponse: isEditing ? editedResponse : undefined,
      });

      toast({
        description: "Your reply has been approved and posted.",
      });

      if (onApprove) onApprove();
      setShowApproveDialog(false);
    } catch (error: any) {
      console.error("Error approving reply:", error);
      toast({
        variant: "destructive",
        description: `Failed to approve: ${error.message || "Unknown error"}`,
      });
    } finally {
      setApproving(false);
    }
  };

  // Reject the reply
  const handleReject = async () => {
    try {
      setRejecting(true);
      await rejectReplyMutation.mutateAsync({
        docId: reply.doc_id,
        personaId: personaData.id,
        reason: rejectReason,
      });

      toast({
        description: "Reply rejected successfully.",
      });

      if (onReject) onReject();
      setShowRejectDialog(false);
    } catch (error: any) {
      console.error("Error rejecting reply:", error);
      toast({
        variant: "destructive",
        description: `Failed to reject: ${error.message || "Unknown error"}`,
      });
    } finally {
      setRejecting(false);
    }
  };

  // Get user details
  const userDetails = reply?.original_message?.user_details || {};
  const username =
    userDetails.username || reply?.original_message?.sender_id || "unknown";
  const displayName = userDetails.name || username;
  const verified = Boolean(userDetails.verified);
  const location = userDetails.location || "";
  const joinedDate = userDetails.created_at || "";
  const followersCount = Number(userDetails.followers_count || 0);
  const tweetText = reply?.original_message?.text || "";
  const mediaUrls = reply?.media_urls || [];
  const threadContext = reply?.thread_context || [];

  return (
    <div className="bg-background border rounded-2xl">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 border-b border-border">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-purple-500/10 text-purple-500"
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Influencer Engagement
            </Badge>
            <span className="text-sm text-muted-foreground">
              {formatDate(reply?.created_at)}
            </span>
          </div>
          <X width={16} height={16} fill="white" />
        </div>

        {/* Original tweet */}
        <div className="border rounded-xl p-3 bg-gray-100 text-black">
          <div className="flex items-start gap-2">
            {/* User Avatar */}
            <div className="flex-shrink-0">
              {userDetails.profile_image_url ? (
                <a
                  href={`https://twitter.com/${username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90 transition-opacity"
                >
                  <img
                    src={userDetails.profile_image_url}
                    alt={`${displayName}'s avatar`}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";

                      // Create fallback icon container
                      const div = document.createElement("div");
                      div.className =
                        "w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center";

                      // Create icon inside container
                      const icon = document.createElement("div");
                      icon.innerHTML =
                        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-500"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>';
                      div.appendChild(icon);

                      // Replace img with div
                      e.currentTarget.parentNode?.replaceChild(
                        div,
                        e.currentTarget,
                      );
                    }}
                  />
                </a>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* User Info Header */}
              <div className="flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <div className="font-bold text-black line-clamp-1">
                      {displayName}
                      {verified && (
                        <span className="ml-1 inline-flex items-center justify-center bg-blue-500 text-white rounded-full w-4 h-4 text-sm">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm">@{username}</div>
                  </div>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {formatDate(reply?.original_message?.timestamp)}
                  </span>
                </div>

                {/* User meta information */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mt-1 mb-2">
                  {location && (
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span>{location}</span>
                    </div>
                  )}
                  {joinedDate && (
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        Joined{" "}
                        {formatDate(joinedDate, {
                          month: "long",
                          year: "numeric",
                          day: undefined,
                          hour: undefined,
                          minute: undefined,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    <span>{formatNumber(followersCount)} followers</span>
                  </div>
                </div>
              </div>

              {/* Tweet content */}
              <p className="mt-1 text-black">{tweetText}</p>

              {/* Display media if available */}
              {mediaUrls && mediaUrls.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {mediaUrls.map((url: string, index: number) => (
                    <div key={index} className="border rounded overflow-hidden">
                      <img
                        src={url}
                        alt="Media attachment"
                        className="w-full h-auto"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Thread context */}
          {threadContext && threadContext.length > 0 && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={openTweetInNewTab}
                className="text-gray-600 hover:text-white hover:bg-gray-800"
              >
                Show full conversation in new tab
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Generated Response Section */}
      <div className="px-6 py-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-base font-medium">Generated Response</h4>
          {!automatedReplies && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {isEditing ? "Cancel Edit" : "Edit Response"}
            </Button>
          )}
        </div>

        {isEditing ? (
          <Textarea
            value={editedResponse}
            onChange={(e) => setEditedResponse(e.target.value)}
            className="min-h-[120px]"
            placeholder="Edit your response..."
          />
        ) : (
          <div className="border rounded-md p-3 bg-muted/20">
            <p className="whitespace-pre-wrap">{reply?.response_content}</p>
          </div>
        )}

        {/* Additional context/info */}
        <div className="mt-4">
          <div className="text-xs text-muted-foreground bg-muted/20 p-2 rounded-md">
            <span className="font-medium">Engagement type:</span> Proactive
            influencer engagement
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-3 border-t flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => setShowRejectDialog(true)}
          disabled={automatedReplies}
        >
          <XCircle className="w-4 h-4 mr-2" />
          Reject
        </Button>
        <Button
          variant="default"
          onClick={() => setShowApproveDialog(true)}
          disabled={automatedReplies}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {isEditing ? "Approve with Edits" : "Approve"}
        </Button>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve and Post Reply</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to approve this reply to{" "}
              <span className="font-medium">@{username}</span>?
            </p>
            <div className="mt-4 bg-muted p-3 rounded-md text-sm">
              <p className="font-medium">Reply content:</p>
              <p className="mt-1 text-muted-foreground">
                {isEditing ? editedResponse : reply?.response_content}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApproveDialog(false)}
              disabled={approving}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={approving}>
              {approving ? "Posting..." : "Approve and Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Reply</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to reject this reply to{" "}
              <span className="font-medium">@{username}</span>?
            </p>
            <div className="mt-4">
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Optional: Add a reason for rejection..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              disabled={rejecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting}
            >
              {rejecting ? "Rejecting..." : "Reject Reply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InfluencerEngagementCard;
